import clsx from 'clsx';
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import Btn from '../actions/Btn';
import type { BtnProps } from '../actions/Btn';
import { IconUpload } from '@icons';

const DEFAULT_PLACEHOLDER = '/image-not-found.jpeg';
const DEFAULT_FALLBACK_ICON = <IconUpload iconClasses="image-upload-preview-icon" />;

export interface ImageUploadProps {
    previewUrl?: string;
    onSelect: (file: File) => void;
    accept?: string;
    disabled?: boolean;
    isUploading?: boolean;
    previewAlt?: string;
    /** When there is no preview (or it fails to load), show these initials instead of the placeholder image. */
    fallbackInitials?: string;
    /** When there is no preview (or it fails to load), show this icon instead of the default upload icon. */
    fallbackIcon?: ReactNode | null;
    placeholderImage?: string;
    uploadLabel?: string;
    changeLabel?: string;
    uploadingLabel?: string;
    className?: string;
    inputId?: string;
    /** Associates the control with an external visible label (`htmlFor` / `aria-labelledby`). */
    labelId?: string;
    inputAriaLabel?: string;
    variant?: NonNullable<BtnProps['variant']>;
    buttonSize?: NonNullable<BtnProps['buttonSize']>;
    buttonClasses?: string;
}

export const ImageUpload = ({
    previewUrl,
    onSelect,
    accept = 'image/*',
    disabled = false,
    isUploading = false,
    previewAlt = 'Image preview',
    fallbackInitials,
    fallbackIcon,
    placeholderImage = DEFAULT_PLACEHOLDER,
    uploadLabel = 'Upload Photo',
    changeLabel = 'Change Photo',
    uploadingLabel = 'Uploading...',
    className,
    inputId,
    labelId,
    inputAriaLabel = 'Choose image file',
    variant = 'secondary-outlined',
    buttonSize = 'md',
    buttonClasses,
}: ImageUploadProps) => {
    const generatedInputId = useId();
    const resolvedInputId = inputId || generatedInputId;
    const statusId = `${resolvedInputId}-status`;
    const inputRef = useRef<HTMLInputElement>(null);
    const [hasPreviewError, setHasPreviewError] = useState(false);

    const resolvedFallbackIcon = fallbackIcon === undefined ? DEFAULT_FALLBACK_ICON : fallbackIcon;

    useEffect(() => {
        setHasPreviewError(false);
    }, [previewUrl]);

    const hasPreview = Boolean(previewUrl) && !hasPreviewError;
    const showInitials = Boolean(fallbackInitials) && !hasPreview;
    const showFallbackIcon = Boolean(resolvedFallbackIcon) && !hasPreview && !showInitials;
    const isControlDisabled = disabled || isUploading;
    const buttonText = isUploading ? uploadingLabel : hasPreview ? changeLabel : uploadLabel;
    const describedBy = isUploading ? statusId : undefined;

    const openFilePicker = () => {
        if (isControlDisabled) {
            return;
        }
        inputRef.current?.click();
    };

    const handlePreviewError = (event: SyntheticEvent<HTMLImageElement>) => {
        if (fallbackInitials || resolvedFallbackIcon) {
            setHasPreviewError(true);
            return;
        }
        event.currentTarget.src = placeholderImage;
    };

    return (
        <div className={clsx('image-upload', className)}>
            <div
                className={clsx(
                    'image-upload-preview',
                    showInitials && 'has-initials',
                    showFallbackIcon && 'has-fallback-icon',
                )}
            >
                {showInitials ? (
                    <span className="image-upload-preview-initials" aria-hidden="true">
                        {fallbackInitials}
                    </span>
                ) : showFallbackIcon ? (
                    <span className="image-upload-preview-icon-wrap" aria-hidden="true">
                        {resolvedFallbackIcon}
                    </span>
                ) : (
                    <img
                        src={previewUrl || placeholderImage}
                        alt={previewAlt}
                        onError={handlePreviewError}
                    />
                )}
            </div>
            <Btn
                variant={variant}
                buttonSize={buttonSize}
                buttonClasses={clsx('image-upload-btn', buttonClasses)}
                buttonText={buttonText}
                buttonDisabled={isControlDisabled}
                buttonOnClick={openFilePicker}
                ariaLabel={inputAriaLabel}
                buttonAriaProps={{
                    'aria-controls': resolvedInputId,
                    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
                }}
            />
            <input
                ref={inputRef}
                id={resolvedInputId}
                type="file"
                accept={accept}
                className="image-upload-input"
                disabled={isControlDisabled}
                aria-label={inputAriaLabel}
                tabIndex={-1}
                {...(labelId ? { 'aria-labelledby': labelId } : {})}
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                        onSelect(file);
                    }
                    event.target.value = '';
                }}
            />
            {isUploading && (
                <span id={statusId} className="image-upload-status" role="status" aria-live="polite">
                    {uploadingLabel}
                </span>
            )}
        </div>
    );
};
